import type React from "react";
import { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import InternetBookingForm from "./InternetAgreementForm";
import MemoryDeliveryNote from "./MemoryBookDeliveryNote";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

interface PrintDialogProps {
  open: boolean;
  onClose: () => void;
  documentType: "internet-form" | "memory-delivery-note";
}

const PrintDocument: React.FC<PrintDialogProps> = ({
  open,
  onClose,
  documentType,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const printRef = useRef<HTMLDivElement>(null);
  const [loadingAction, setLoadingAction] = useState<
    "print" | "download" | null
  >(null);

  const documentTitle =
    documentType === "internet-form"
      ? "Internet-Agreement-Form"
      : "Memory-Book-Delivery-Note";

  const generatePDF = async (): Promise<jsPDF | null> => {
    if (!printRef.current) {
      toast.error("No content to generate");
      return null;
    }

    try {
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const canvas = await html2canvas(printRef.current, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: printRef.current.scrollWidth,
        windowHeight: printRef.current.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.85);

      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

      return pdf;
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Document generation failed");
      return null;
    }
  };

  const handlePrint = async () => {
    setLoadingAction("print");
    try {
      const pdf = await generatePDF();
      if (!pdf) return;

      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Pop-up blocked! Allow pop-ups to print.");
        URL.revokeObjectURL(pdfUrl);
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print ${documentTitle}</title>
          <style>
            body { margin: 0; overflow: hidden; }
            iframe { width: 100vw; height: 100vh; border: none; }
          </style>
        </head>
        <body>
          <iframe src="${pdfUrl}"></iframe>
        </body>
        </html>
      `);
      printWindow.document.close();

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
    } catch (error) {
      console.error("Print failed:", error);
      toast.error("Print preparation failed");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownload = async () => {
    setLoadingAction("download");
    try {
      const pdf = await generatePDF();
      if (pdf) {
        pdf.save(`${documentTitle}.pdf`);
        toast.success("PDF downloaded successfully");
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed");
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight="700">
            {documentType === "internet-form"
              ? "Internet Form Preview"
              : "Memory Delivery Note Preview"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography
          variant="body2"
          sx={{ mt: 1, color: reduxTheme.primary_colors?.[700] }}
        >
          Preview and print the document
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box ref={printRef}>
          {documentType === "internet-form" ? (
            <InternetBookingForm />
          ) : (
            <MemoryDeliveryNote />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
        <Button
          onClick={handleDownload}
          variant="outlined"
          startIcon={
            loadingAction === "download" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          disabled={loadingAction !== null}
        >
          {loadingAction === "download" ? "Downloading..." : "Download PDF"}
        </Button>

        <Button
          onClick={handlePrint}
          variant="contained"
          startIcon={
            loadingAction === "print" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PrintIcon />
            )
          }
          disabled={loadingAction !== null}
        >
          {loadingAction === "print" ? "Preparing..." : "Print"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintDocument;